import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Camera, Sparkles, Loader2, AlertCircle, Link2 } from "lucide-react";
import {
  FLUX_STYLES, FLUX_SIZES,
  type FluxStyle, type FluxSize,
} from "../../data/generated/flux-prompts.generated";
import { composeFluxPrompt, renderAndWait, type RenderResult } from "../../lib/comfy/client";
import { cls } from "../../lib/cls";
import {
  readProjectRenderContext,
  composeGroundedFragment,
  citationsFor,
  suggestPrompts,
  clientShowPrompts,
  type ProjectRoomBinding,
  type PromptSuggestion,
} from "../../lib/projectGrounding";

/* Render-this-room panel — Apple-style, grounded in the project graph.
   The style/room/material chips you see here are not hard-coded UI
   options — they are read from SAMPLE_PROJECT at click time, exactly
   like the rest of the cockpit. Maya picks a room → we inject the
   Intent + Materials + room SKUs into the FLUX prompt with citations
   so every pixel can be traced back to a graph node. */

export function RenderRoomPanel(): JSX.Element {
  const ctx = useMemo(() => readProjectRenderContext(), []);
  const [style, setStyle] = useState<FluxStyle>(ctx.recommendedStyle);
  const firstRoom = ctx.rooms[0] ?? null;
  const [roomId, setRoomId] = useState<string>(firstRoom?.room.id ?? "");
  const [size, setSize] = useState<FluxSize>(FLUX_SIZES[1]!);
  const [extra, setExtra] = useState("");
  const [includeSkus, setIncludeSkus] = useState(true);
  const [audience, setAudience] = useState<"designer" | "client">("designer");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<RenderResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const binding = useMemo<ProjectRoomBinding | null>(
    () => ctx.rooms.find((r) => r.room.id === roomId) ?? null,
    [ctx.rooms, roomId],
  );

  const groundedFragment = useMemo(
    () => composeGroundedFragment({ ctx, roomId, includeSkus }),
    [ctx, roomId, includeSkus],
  );

  // We hand-build the customPrompt so the user sees that the
  // grounded fragment is what makes their render Foyr-specific.
  const composed = useMemo(() => {
    const customPrompt = [groundedFragment, extra].filter(Boolean).join(" ").trim();
    return composeFluxPrompt({
      style,
      roomCue: binding?.cue ?? null,
      size,
      ...(customPrompt ? { customPrompt } : {}),
    });
  }, [style, binding, size, groundedFragment, extra]);

  const citations = useMemo(() => citationsFor(ctx, roomId), [ctx, roomId]);

  const suggestions = useMemo<readonly PromptSuggestion[]>(
    () => audience === "designer"
      ? suggestPrompts({ ctx, roomId, count: 6 })
      : clientShowPrompts({ ctx, roomId, count: 5 }),
    [ctx, roomId, audience],
  );

  async function run(): Promise<void> {
    setBusy(true); setError(null); setResult(null);
    try {
      const customPrompt = [groundedFragment, extra].filter(Boolean).join(" ").trim();
      const r = await renderAndWait({
        style,
        roomCue: binding?.cue ?? null,
        size,
        ...(customPrompt ? { customPrompt } : {}),
      });
      setResult(r);
      if (r.status === "error") setError(r.error ?? "render failed");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-2xl border border-border/70 bg-gradient-to-br from-cream via-cream to-cream-dark/30 shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_24px_-12px_rgba(0,0,0,0.08)] overflow-hidden mb-4">
      <header className="px-4 pt-4 pb-2 flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow inline-flex items-center gap-1.5">
            <Camera className="w-3 h-3" /> Text → room · FLUX, grounded in your project
          </div>
          <h3 className="text-[16px] font-semibold text-ink-950 mt-0.5 leading-tight">
            Render this room — using {ctx.project?.label ?? "the project"}&apos;s intent &amp; materials.
          </h3>
          <p className="text-[12px] text-ink-500 mt-0.5">
            We auto-inject {ctx.intent?.label ?? "the brief"}, your selected style, materials and the SKUs already placed in this room.
          </p>
        </div>
      </header>

      {/* Grounded-in citation strip — every chip cites a graph node. */}
      <div className="px-4 pb-2 -mt-1">
        <div className="flex items-center gap-2 flex-wrap text-[10.5px]">
          <span className="uppercase tracking-wider text-ink-500 inline-flex items-center gap-1">
            <Link2 className="w-3 h-3" /> grounded in
          </span>
          {ctx.project ? (
            <span className="pill border border-foyr/30 bg-foyr/5 text-foyr-deep">
              Project · {ctx.project.label}
            </span>
          ) : null}
          {ctx.intent ? (
            <span className="pill border border-fern/30 bg-fern/5 text-ink-700">
              Intent · {ctx.intent.label}
            </span>
          ) : null}
          {ctx.styles.map((s) => (
            <span key={s.id} className="pill border border-border bg-cream-dark text-ink-700">
              Style · {s.label}
            </span>
          ))}
          {ctx.materials.map((m) => (
            <span key={m.id} className="pill border border-border bg-cream-dark text-ink-700">
              Material · {m.label}
            </span>
          ))}
        </div>
      </div>

      <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider text-ink-500">Style ({style.id === ctx.recommendedStyle.id ? "from graph" : "manual override"})</span>
            <div className="mt-1 flex flex-wrap gap-1">
              {FLUX_STYLES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStyle(s)}
                  className={cls(
                    "pill text-[11px] border inline-flex items-center gap-1.5",
                    style.id === s.id
                      ? "bg-ink-950 text-cream border-ink-950"
                      : "bg-cream-dark border-border text-ink-700 hover:border-foyr/40",
                  )}
                  title={s.id === ctx.recommendedStyle.id ? "Picked from your project's Style node" : s.label}
                >
                  <span className="flex">
                    {s.swatches.slice(0, 4).map((c, i) => (
                      <span key={i} className="block w-2 h-2 rounded-full ring-1 ring-cream/80 -ml-0.5 first:ml-0" style={{ background: c }} />
                    ))}
                  </span>
                  {s.label}
                </button>
              ))}
            </div>
          </label>

          <label className="block mt-3">
            <span className="text-[10.5px] uppercase tracking-wider text-ink-500">Room · from this project</span>
            <div className="mt-1 flex flex-wrap gap-1">
              {ctx.rooms.length === 0 ? (
                <span className="text-[11px] text-ink-500">No rooms in the graph yet.</span>
              ) : ctx.rooms.map((b) => (
                <button
                  key={b.room.id}
                  type="button"
                  onClick={() => setRoomId(b.room.id)}
                  className={cls(
                    "pill text-[11px] border",
                    roomId === b.room.id
                      ? "bg-ink-950 text-cream border-ink-950"
                      : "bg-cream-dark border-border text-ink-700 hover:border-foyr/40",
                  )}
                  title={`${(ctx.skusByRoom[b.room.id] ?? []).length} SKUs in graph · cue: ${b.cue?.kind ?? "auto"}`}
                >
                  {b.room.label}
                  <span className="ml-1 text-ink-400">·</span>
                  <span className="ml-0.5 text-ink-400 font-mono">{(ctx.skusByRoom[b.room.id] ?? []).length}</span>
                </button>
              ))}
            </div>
          </label>

          <label className="block mt-3">
            <span className="text-[10.5px] uppercase tracking-wider text-ink-500">Size</span>
            <div className="mt-1 flex flex-wrap gap-1">
              {FLUX_SIZES.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setSize(s)}
                  className={cls(
                    "pill text-[11px] border",
                    size.id === s.id
                      ? "bg-ink-950 text-cream border-ink-950"
                      : "bg-cream-dark border-border text-ink-700 hover:border-foyr/40",
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </label>

          <label className="block mt-3 inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeSkus}
              onChange={(e) => setIncludeSkus(e.target.checked)}
              className="accent-foyr w-3.5 h-3.5"
            />
            <span className="text-[11px] text-ink-700">
              Include SKUs from this room
              {binding ? (
                <span className="ml-1 text-ink-500">
                  ({(ctx.skusByRoom[binding.room.id] ?? []).length})
                </span>
              ) : null}
            </span>
          </label>

          <div className="mt-3">
            <div className="flex items-center justify-between">
              <span className="text-[10.5px] uppercase tracking-wider text-ink-500">
                Suggestions · from your graph
              </span>
              <div className="inline-flex rounded-full bg-cream-dark border border-border p-0.5 text-[10px]">
                <button
                  type="button"
                  onClick={() => setAudience("designer")}
                  className={cls(
                    "px-2 py-0.5 rounded-full transition-colors",
                    audience === "designer" ? "bg-ink-950 text-cream" : "text-ink-600",
                  )}
                >
                  Designer · innovate
                </button>
                <button
                  type="button"
                  onClick={() => setAudience("client")}
                  className={cls(
                    "px-2 py-0.5 rounded-full transition-colors",
                    audience === "client" ? "bg-ink-950 text-cream" : "text-ink-600",
                  )}
                >
                  Client · final feel
                </button>
              </div>
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              {suggestions.length === 0 ? (
                <span className="text-[11px] text-ink-500">Pick a room to see suggestions.</span>
              ) : suggestions.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => setExtra(s.fragment)}
                  className="pill text-[11px] border border-border bg-cream text-ink-700 hover:border-foyr/40 hover:text-ink-950 inline-flex items-center gap-1"
                  title={s.fragment + (s.fromNodeIds.length > 0 ? `\n— cited: ${s.fromNodeIds.join(", ")}` : "")}
                >
                  <Sparkles className="w-3 h-3 text-foyr" />
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <label className="block mt-3">
            <span className="text-[10.5px] uppercase tracking-wider text-ink-500">Extra details (free text)</span>
            <input
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder={audience === "designer"
                ? "e.g. afternoon light, abstract art on the back wall"
                : "e.g. our first Diwali, family around the table"}
              className="mt-1 w-full text-xs bg-cream border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-foyr/30"
            />
          </label>

          <button
            type="button"
            onClick={run}
            disabled={busy}
            className="btn btn-foyr mt-3 w-full !text-xs !py-2"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {busy ? "Rendering…" : `Render ${binding?.room.label ?? "room"}`}
          </button>

          {error ? (
            <div className="mt-2 rounded-md border border-foyr/30 bg-foyr/5 px-2 py-1.5 text-[11px] text-foyr-deep inline-flex items-start gap-1">
              <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
              {error}. The prompt was composed and grounded — once the Cloudflare Tunnel to ComfyUI is wired (<code className="font-mono">comfy.hypergraphmind.com</code>), this will return real renders.
            </div>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[10.5px] uppercase tracking-wider text-ink-500">Grounded prompt</div>
            <div className="text-[10px] text-ink-400">FLUX-Schnell · 4 steps · cfg 1.0</div>
          </div>
          <pre className="rounded-lg border border-border bg-cream-dark/40 p-2 text-[10.5px] text-ink-700 leading-snug whitespace-pre-wrap max-h-[200px] overflow-auto font-mono">
            <span className="text-foyr-deep">{groundedFragment ? groundedFragment + " " : ""}</span>
            <span>{composed.positive.slice((groundedFragment ? groundedFragment.length + 1 : 0))}</span>
          </pre>

          {citations.length > 0 ? (
            <div>
              <div className="text-[10.5px] uppercase tracking-wider text-ink-500 mb-1">
                Cited from graph ({citations.length})
              </div>
              <div className="flex flex-wrap gap-1">
                {citations.map((c) => (
                  <span
                    key={c.fromNodeId + c.fragment}
                    className="pill text-[10px] border border-border bg-cream text-ink-700"
                    title={`${c.fromKind} · ${c.fromNodeId}`}
                  >
                    <span className="text-ink-400 mr-1 font-mono">{c.fromKind[0]}</span>
                    {c.fragment}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div className="text-[10.5px] uppercase tracking-wider text-ink-500 mt-2">Negative prompt</div>
          <pre className="rounded-lg border border-border bg-cream-dark/40 p-2 text-[10.5px] text-ink-700 leading-snug whitespace-pre-wrap max-h-[80px] overflow-auto font-mono">{composed.negative}</pre>
        </div>
      </div>

      {result && result.image_urls.length > 0 ? (
        <div className="px-4 pb-4">
          <div className="text-[10.5px] uppercase tracking-wider text-ink-500 mb-1.5">Render</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {result.image_urls.map((u, i) => (
              <motion.img
                key={u}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                src={u}
                alt="FLUX render"
                className="rounded-xl border border-border shadow-sm w-full"
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
